import CustomImage from "@components/custom-image";
import { productImageCandidates } from "@provider/image-url";
import AnimatedLink from "@components/animated-link";
import ProductAmount from "@components/product-amount";
import ProductAmountWishlist from "@components/product-amount-wishlist";
import { roundness } from "@provider/dripsy";
import type { Recipe } from "@type/recipe";
import type { Wishlist } from "@type/wishlist";
import { moneyFormatter, notSelectableOnWebStyle, useDripsyColor } from "@utils";
import { Text, useSx, View } from "dripsy";
import { LinearGradient } from "expo-linear-gradient";
import * as React from "react";

import Price from "./price";
import routes from "../../NavigatorRoutes";
import type { ProductType } from "@type/global";
import FavouriteModalButton from "@features/favourite/favourite-modal/favourite-modal-button";
import { useStatiegeld } from "@provider/app-data";

export const ITEM_HEIGHT_ARRAY = [90, 100, 130];

function ProductItem({
  product,

  wishlistItem,
  hideFav = false,
  recipe,
}: {
  product: ProductType;
  wishlistItem?: Wishlist;

  hideFav?: boolean;
  recipe?: Recipe;
}) {
  const statiegeld = useStatiegeld()?.data?.find((it) => it.statiegeld_id === product.pid);

  const isStampProduct = product.zegels > 0;
  const isStatiegeldProduct = product.pid < 0;
  const effectiveStock = product.voorraad;
  const isOutOfStock =
    effectiveStock !== undefined && effectiveStock !== null && effectiveStock <= 0 && product.leverbaar === 1;

  const sx = useSx();

  const imagePadding = 10;
  const imageSize = [65, 85, 105];
  const imageSizeInner = imageSize.map((is) => is - imagePadding);

  const isRecipeItem = recipe !== undefined;

  const Component = product.leverbaar === 1 || isStampProduct || isStatiegeldProduct ? AnimatedLink : View;
  const pressedColor = useDripsyColor({ color: "$lightGrey" });
  const normalColor = useDripsyColor({ color: isRecipeItem ? "$secondaryBackground" : "$background" });

  // if (process.env.NODE_ENV === "development") {
  //   product.omschr1 = "Regionaal productje";
  // }
  return (
    <View testID={`product-${product.pid}`}>
      <Component
        to={routes.ProductDetailScreen}
        params={{ id: `${product.id}` }}
        animate={React.useMemo(
          () =>
            ({ hovered, pressed }) => {
              return {
                borderColor: hovered || pressed ? pressedColor : normalColor,
              };
            },
          [pressedColor, normalColor],
        )}
        style={sx({
          height: ITEM_HEIGHT_ARRAY,
          p: ["$2", "$3", "$4"],
          alignItems: "center",
          flexDirection: "row",
          borderRadius: roundness,
          borderColor: "#ffffff",
          borderWidth: 1,
        })}
      >
        <View
          sx={{
            width: imageSize,
            height: imageSize,
            // backgroundColor: "#fff",
            borderRadius: roundness,
            ...notSelectableOnWebStyle,
          }}
        >
          <CustomImage
            source={productImageCandidates(product.pid, product, 360)}
            contentFit="contain"
            sx={{
              height: imageSizeInner,
              width: imageSizeInner,
              borderRadius: roundness,
              margin: imagePadding / 2,
              opacity: isOutOfStock ? 0.5 : 1,
            }}
            alt={product.pnm}
          />
        </View>

        <View
          sx={{
            flex: 1,
            px: ["$2", "$3"],
            mr: ["$4", "$5"],
          }}
        >
          <Text sx={{ fontSize: [15, 16, 18], fontWeight: "700" }} allowFontScaling={false}>
            {product.pnm}
          </Text>
          {product.omschr1 ? (
            <Text sx={{ color: "$textGrey" }} numberOfLines={1}>
              {product.omschr1}
            </Text>
          ) : null}
          <View sx={{ flexDirection: "row", alignItems: "flex-end", marginTop: "$1" }}>
            {isStampProduct ? (
              <>
                <Text sx={{ fontWeight: "600" }}>{product.zegels}</Text>
                <Text sx={{ color: "$textSecondary" }}> zegels en </Text>
                <Text sx={{ fontWeight: "600" }}>{moneyFormatter.format(product.ppeg)}</Text>
              </>
            ) : isStatiegeldProduct ? (
              <Price ppeg={product.statiegeld} actie={0} actieprijs={0} />
            ) : (
              <Price actie={product.actie} actieprijs={product.actieprijs} ppeg={product.ppeg} />
            )}
            <View sx={{ marginLeft: isStampProduct ? "$1" : "$2", marginBottom: 0 }}>
              <Text
                sx={{
                  fontSize: [11, 12, 14],
                  fontWeight: "400",
                  color: "$textGrey",
                }}
              >
                {product.pertext}
              </Text>
            </View>
          </View>
        </View>
      </Component>
      <LinearGradient
        colors={["rgba(255,255,255,0)", "rgba(255,255,255,0.85)", "rgba(255,255,255,1)"]}
        locations={[0, 0.3, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={sx({
          alignItems: "flex-end",
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          justifyContent: "center",
          paddingRight: [12, 24],
          paddingLeft: [20, 30],
        })}
      >
        {isStampProduct || isStatiegeldProduct || hideFav ? (
          <View sx={{ height: 0 }} />
        ) : (
          <FavouriteModalButton product={product} />
        )}
        {isOutOfStock ? (
          <View
            sx={{
              backgroundColor: "$secondaryBackground",
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 16,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text sx={{ fontWeight: "500", color: "$textSecondary", fontSize: [11, 12, 13] }}>Uitverkocht</Text>
          </View>
        ) : product.leverbaar === 1 || isStampProduct || isStatiegeldProduct ? (
          <>
            {wishlistItem ? (
              <ProductAmountWishlist product={product} wishlist={wishlistItem} />
            ) : (
              <ProductAmount product={product} recipeId={recipe?.id} statiegeld={statiegeld} />
            )}
          </>
        ) : (
          <Text sx={{ fontWeight: "500" }}>Niet leverbaar</Text>
        )}
      </LinearGradient>
    </View>
  );
}

type ProductItemProps = React.ComponentProps<typeof ProductItem>;

// De productlijst maakt per groepering nieuwe objecten ({...p, extraCatKey})
// met dezelfde velden: vergelijk het product daarom veld voor veld (ondiep).
function sameProductItemProps(prev: ProductItemProps, next: ProductItemProps): boolean {
  return (
    prev.wishlistItem === next.wishlistItem &&
    prev.hideFav === next.hideFav &&
    prev.recipe === next.recipe &&
    shallowEqual(prev.product, next.product)
  );
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  if (a === b) return true;
  const keys = Object.keys(a) as (keyof T)[];
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}

export default React.memo(ProductItem, sameProductItemProps);
